import { pluralize } from 'inflected';
import type WhiteboardController from '../controllers/WhiteboardController';
import type Shape from '../objects/Shape';
import type ShapeSmartRecord from '../shapes/ShapeSmartRecord';
import type {
  CreateRecordData,
  CreateRecordMeta,
  CreateRecordParentId,
  IRecordManager,
  RecordAttributes,
  WhiteboardRecordInfo,
} from '../shared-types';
import type { CreatedRecordMetadata, SmartRecordCreatableType } from '../types';
import trackEvent from '../utils/analytics';
import { sortShapesLTR } from '../utils/shapes';
import type SmartRecordDataManager from './SmartRecordDataManager';
import { capitalize } from 'lodash';
import * as Sentry from '@sentry/browser';
import { recordTypeAsField, type FieldsValue } from './SmartRecordDataManager';

export const CREATE_ORDER = [
  'goals',
  'initiatives',
  'releases',
  'epics',
  'features',
  'requirements',
  'ideas',
] as const;

// Handles the conversion of SmartRecord shapes to Aha! records and parses the
// results into successfully converted and failed records
class SmartRecordConverter {
  convertedRecords: WhiteboardRecordInfo[];
  failedRecords: Shape<ShapeSmartRecord>[];
  // Response from server containing shape id and recordId for the converted shape
  resultsByType: Record<SmartRecordCreatableType, CreatedRecordMetadata[]>;

  constructor(
    private readonly dataManager: SmartRecordDataManager,
    private readonly recordManager: IRecordManager | undefined,
    private readonly fetchRecordInfo: WhiteboardController['fetchRecordInfo']
  ) {
    this.reset();
  }

  reset() {
    this.convertedRecords = [];
    this.failedRecords = [];
    this.resultsByType = {
      ideas: [],
      features: [],
      requirements: [],
      epics: [],
      releases: [],
      initiatives: [],
      goals: [],
    };
  }

  get results() {
    return Object.keys(this.resultsByType).reduce(
      (acc: CreatedRecordMetadata[], key: string) => {
        acc.push(...this.resultsByType[key]);
        return acc;
      },
      []
    );
  }

  get convertedRecordTypes() {
    return Object.keys(this.resultsByType)
      .filter(key => this.resultsByType[key].length > 0)
      .sort((a, b) => CREATE_ORDER.indexOf(a) - CREATE_ORDER.indexOf(b));
  }

  async convertRecords(
    progressCallback: (recordType: SmartRecordCreatableType | '') => void
  ) {
    const types = this.sortedRecordTypes;

    for (const recordType of types) {
      if (this.dataManager.recordSelectedForType(recordType)) {
        progressCallback(recordType);
        try {
          const createdRecords = await this.createRecordsOfType(recordType);
          if (createdRecords) {
            this.resultsByType[recordType] = createdRecords;
          }
        } catch (error) {
          console.error('error converting records', error);
          Sentry.captureException(error.message);
          break;
        }
      }
    }

    progressCallback('');

    trackEvent('Feature-7366764257171025062', 'User converts Aha! shape', {
      'Number of shapes converted': this.convertedRecords.length,
      'Number of failed conversions': this.failedRecords.length,
    });
  }

  // For records that were successfully created, fetch the record info
  // Get a list of smart shapes that failed to convert
  async processConversionResults() {
    this.convertedRecords = await this.fetchConvertedRecords();
    this.failedRecords = this.getFailedRecords();
  }

  private createRecordsOfType(recordType: SmartRecordCreatableType) {
    if (this.recordManager?.canCreateRecordType(recordType)) {
      const records = this.recordsForTypeForSubmit(recordType);
      const createFunc = `create${capitalize(recordType)}`;
      return this.recordManager[createFunc](records);
    }
  }

  // Normalizes records for conversion. This will put the required fields for a
  // record in the expected format, along with meta data needed for parsing results
  private recordsForTypeForSubmit(
    type: SmartRecordCreatableType
  ): CreateRecordData[] {
    // Get selected records
    const records = this.dataManager
      .recordsForTypeAsArray(type)
      .filter(record => this.dataManager.recordSelected(record));

    // Add meta and args for each record.
    return records.map(record => this.getRecordData(record)).filter(Boolean);
  }

  private async fetchConvertedRecords() {
    const records = this.dataManager.allRecords;

    // Lookup the Aha! record info for each converted record
    // const promises = this.results
    const promises = this.results
      .map(async result => {
        const record = records.find(record => record.id === result.id);
        if (record) {
          return await this.fetchRecordInfo(
            record.recordType!,
            result.recordId
          );
        }
      })
      .filter(Boolean);

    return (await Promise.all(promises)).filter(Boolean);
  }

  // Find the smart shapes that failed to convert to Aha! records
  private getFailedRecords() {
    const types = this.dataManager.availableRecordTypes;
    const selectedRecordCount = this.dataManager.recordSelectedCount;
    if (selectedRecordCount === this.results.length) return [];

    const records = types.reduce(
      (
        acc: Shape<ShapeSmartRecord>[],
        type: SmartRecordCreatableType
      ): Shape<ShapeSmartRecord>[] => {
        // Filter through records, getting records that were selected and do not have a result
        const failedRecords = this.dataManager
          .recordsForTypeAsArray(type)
          .filter(record => {
            if (!this.dataManager.recordSelected(record)) return false;
            const result = this.results.find(result => result.id === record.id);
            return !result;
          });

        acc.push(...failedRecords);
        return acc;
      },
      []
    );
    return sortShapesLTR(records);
  }

  // This method puts together the required key, id, and any attributes this record
  // may have. The required key/id could exist on this record's parent smart shape,
  // in an ancestor smart shape, or in the user selected fields for this record
  // type, so we have to check all of these possibilites
  private getRecordData(record: any): CreateRecordData | null {
    const meta: CreateRecordMeta = { id: record.id };
    const requiredFields = this.dataManager.requiredFieldsForRecordType(
      record.recordType as SmartRecordCreatableType
    );
    const fields = this.dataManager.getRecordFields(record);
    const parent = this.dataManager.getParent(record);
    let requiredKey: CreateRecordParentId | undefined;
    let requiredId: RecordId | undefined;

    // Parse the parent smart shape, setting the required key/id if applicable
    const parentResult = parent
      ? this.getRequiredFieldsFromParent(parent, requiredFields)
      : undefined;
    if (parentResult) {
      requiredKey = parentResult.requiredKey;
      requiredId = parentResult.requiredId;
    }

    // If the parent did not have the required key/id, check the fields
    let fieldsKeyAndId;
    if (
      (!requiredKey || !requiredId) &&
      (fieldsKeyAndId = this.getRequiredKeyAndId(fields, requiredFields)) !==
        undefined
    ) {
      if (fieldsKeyAndId.requiredKey && fieldsKeyAndId.requiredId) {
        requiredKey = fieldsKeyAndId.requiredKey;
        requiredId = fieldsKeyAndId.requiredId;
      }
    }

    // If we could not find the required key/id, do not convert this smart shape
    if (!requiredId || !requiredKey) {
      return null;
    }

    const attributes = this.getAttributes(record, fields);

    return {
      meta,
      attributes,
      name: record.plainText(),
      [requiredKey]: requiredId,
    };
  }

  getRequiredFieldsFromParent(parent: Shape<ShapeSmartRecord>, requiredFields) {
    const parentResult = this.getConvertedShape(parent);
    if (parentResult) {
      const recordTypeSingular = recordTypeAsField(
        parent.recordType as SmartRecordCreatableType
      );
      if (requiredFields[recordTypeSingular]) {
        return {
          requiredKey: `${recordTypeSingular}Id` as CreateRecordParentId,
          requiredId: parentResult.recordId,
        };
      }
    }
  }

  private getAttributes(shape: Shape<ShapeSmartRecord>, fields: FieldsValue) {
    const requiredFields = this.dataManager.requiredFieldsForRecordType(
      shape.recordType as SmartRecordCreatableType
    );

    const attributes: RecordAttributes = {};
    Object.entries(fields).forEach(([field, value]) => {
      if (value?.isSmartShape && !requiredFields[field]) {
        const smartShape = this.dataManager.findRecord(value.id);
        if (smartShape) {
          const convertedShape = this.getConvertedShape(smartShape);
          if (convertedShape) {
            // Goals are always has_many, and initiatives are has_many for releases
            if (
              field === 'goal' ||
              (shape.recordType === 'releases' && field === 'initiative')
            ) {
              attributes[pluralize(field)] = [{ id: convertedShape.recordId }];
            } else {
              // Everything else is a belongs_to relationship
              attributes[field] = { id: convertedShape.recordId };
            }
          }
        }
      }
    });

    return attributes;
  }

  // Finds a converted shape for a given smart record
  private getConvertedShape(
    shape: Shape<ShapeSmartRecord>
  ): CreatedRecordMetadata | undefined {
    const recordType = shape.recordType as SmartRecordCreatableType;

    // Get result of converted shape record
    return this.resultsByType[recordType].find(
      result => result.id === shape.id
    );
  }

  private getRequiredKeyAndId(
    fields: Record<string, any>,
    requiredFields: Record<string, any>
  ): { requiredKey?: CreateRecordParentId; requiredId?: RecordId } | undefined {
    for (const field of Object.keys(requiredFields)) {
      if (!requiredFields[field]) {
        continue;
      }

      const value = fields[field];
      if (value) {
        if (value.isSmartShape) {
          // If the field comes from a smart shape, we need to find the converted
          // record for that shape and use its recordId as the requiredId
          const result = this.resultsByType[pluralize(field)].find(
            result => result.id === value.id
          );
          if (!result) {
            return;
          }
          return {
            requiredKey: `${field}Id` as CreateRecordParentId,
            requiredId: result.recordId,
          };
        } else {
          // Field is a regular, user selected field. Just use the values from that
          return {
            requiredKey: `${field}Id` as CreateRecordParentId,
            requiredId: fields[field].id,
          };
        }
      }
    }

    return undefined;
  }

  private get sortedRecordTypes() {
    return this.dataManager.availableRecordTypes.sort(
      (a, b) => CREATE_ORDER.indexOf(a) - CREATE_ORDER.indexOf(b)
    );
  }
}

export default SmartRecordConverter;
